package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Large tappable option card for single-choice pickers (onboarding language/city, future surveys):
 * white card with 1dp outline; selected = 2dp primary border + light-teal fill + trailing check.
 * [supportingText] renders a muted second line (e.g. a province). [enabled] false dims the card and
 * shows [disabledBadge] (localized, e.g. "Coming soon") instead of the check slot. All copy comes
 * from the caller — the component ships none.
 */
@Composable
fun AppSelectableCard(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    supportingText: String? = null,
    enabled: Boolean = true,
    disabledBadge: String? = null,
    centered: Boolean = false,
    // Override for cross-language labels (e.g. an endonym in another script needs its own font
    // family on web — see `fontFamilyFor`). Defaults to the theme's headlineMd.
    labelStyle: androidx.compose.ui.text.TextStyle? = null,
) {
    val border = if (selected) {
        BorderStroke(AppTheme.sizes.strokeSm, AppTheme.colors.primary)
    } else {
        BorderStroke(AppTheme.sizes.borderWidth, AppTheme.colors.outline)
    }
    val container = when {
        !enabled -> AppTheme.colors.surfaceContainer
        selected -> AppTheme.colors.categoryChip
        else -> AppTheme.colors.surface
    }
    val labelColor = if (enabled) AppTheme.colors.onSurface else AppTheme.colors.onSurfaceVariant
    Row(
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = AppTheme.sizes.minTouchTarget)
            .background(container, AppTheme.shapes.card)
            .border(border, AppTheme.shapes.card)
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        Column(
            modifier = Modifier.weight(1f),
            horizontalAlignment = if (centered) Alignment.CenterHorizontally else Alignment.Start,
        ) {
            Text(
                text = label,
                style = labelStyle ?: AppTheme.typography.headlineMd,
                color = labelColor,
                textAlign = if (centered) TextAlign.Center else null,
            )
            if (supportingText != null) {
                Text(
                    text = supportingText,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    textAlign = if (centered) TextAlign.Center else null,
                )
            }
            // The badge sits UNDER the label, not beside it: a wide "Coming soon" pill as a horizontal
            // sibling steals the row's width and collapses a long label (e.g. "Greater Los Angeles")
            // to one glyph per line in the narrow 2-up city grid. Stacked, the label always gets full width.
            if (!enabled && disabledBadge != null) {
                Spacer(Modifier.height(AppTheme.spacing.xs))
                StatusBadge(label = disabledBadge)
            }
        }
        if (enabled && selected) {
            AppIcon(
                name = "check_circle",
                size = AppTheme.sizes.iconMd,
                tint = AppTheme.colors.primary,
            )
        }
    }
}

@Preview
@Composable
private fun AppSelectableCardPreview() {
    AppTheme {
        Column {
            AppSelectableCard(label = "English", selected = true, onClick = {})
            AppSelectableCard(label = "Français", selected = false, onClick = {})
            AppSelectableCard(
                label = "Calgary",
                supportingText = "AB",
                selected = false,
                enabled = false,
                disabledBadge = "Coming soon",
                onClick = {},
            )
        }
    }
}
